import ReactGA from "react-ga4"; // Updated to GA4
import Hotjar from "@hotjar/browser"; // Fixed import
import { useEffect } from "react";
import { useLocation } from 'react-router-dom';
// Note: VercelAnalytics is a React component and should be used in App.tsx

// Environment detection
const isProduction = process.env.NODE_ENV === "production";
const isDevelopment = process.env.NODE_ENV === "development";

// Configuration
const ANALYTICS_CONFIG = {
  GA_ID: process.env.REACT_APP_GOOGLE_ANALYTICS_ID || "G-9LMVVXFRGM",
  HOTJAR_ID: parseInt(process.env.REACT_APP_HOTJAR_ID || "5246605", 10),
  HOTJAR_VERSION: 6,
  DEBUG: isDevelopment,
  ENABLED: isProduction,
  BACKEND_URL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000",
};

// Analytics state
let analyticsInitialized = false;
let gaInitialized = false;
let hotjarInitialized = false;

// Vercel Analytics is a React component, not a class to instantiate

// Backend analytics sender
const sendToBackendAnalytics = async (data: any): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    const headers: any = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${ANALYTICS_CONFIG.BACKEND_URL}/api/analytics`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...data,
        sessionId: getSessionId(),
        clientTimestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });
  } catch (error) {
    // Silently fail - analytics should not break the app
    if (ANALYTICS_CONFIG.DEBUG) {
      console.error('Backend analytics failed:', error);
    }
  }
};

// Get or create session ID
export const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analyticsSessionId');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analyticsSessionId', sessionId);
  }
  return sessionId;
};

// Analytics service for tracking user events and page views
class Analytics {
  private static instance: Analytics;
  private initialized = false;
  private events: { [key: string]: number } = {};

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public initialize(): void {
    if (this.initialized) return;
    
    // Initialize analytics service
    this.initialized = true;
    console.log('Analytics service initialized');
  }

  public trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('Analytics not initialized. Call initialize() first.');
      return;
    }

    // Track event
    this.events[eventName] = (this.events[eventName] || 0) + 1;
    console.log(`Event tracked: ${eventName}`, properties || '');
  }

  public trackPageView(path: string): void {
    if (!this.initialized) {
      console.warn('Analytics not initialized. Call initialize() first.');
      return;
    }

    // Track page view
    console.log(`Page view: ${path}`);
  }

  public getEventCount(eventName: string): number {
    return this.events[eventName] || 0;
  }

  // Unified track method for events and page views
  public track(eventName: string, properties?: Record<string, any>): void {
    if (eventName === 'page_view') {
      this.trackPageView(properties?.path || '');
    } else {
      this.trackEvent(eventName, properties);
    }
  }

  // User identification method for analytics
  public identify(userId: string, traits?: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('Analytics not initialized. Call initialize() first.');
      return;
    }
    // Implement user identification logic here, or just log for now
    console.log(`User identified: ${userId}`, traits || '');
  }
}

// Export a singleton instance
const analyticsInstance = Analytics.getInstance();

/**
 * Initialize all analytics platforms
 */
export const initializeAnalytics = (): void => {
  if (analyticsInitialized) return;

  // Debug information
  console.log("Environment Status:");
  console.log(`NODE_ENV: ${process.env.NODE_ENV}`);
  console.log(
    `Google Client ID set: ${!!process.env.REACT_APP_GOOGLE_CLIENT_ID}`,
  );
  console.log(`Google Analytics ID set: ${!!ANALYTICS_CONFIG.GA_ID}`);
  console.log(`Hotjar ID set: ${!!ANALYTICS_CONFIG.HOTJAR_ID}`);

  // Initialize Google Analytics
  if (ANALYTICS_CONFIG.GA_ID) {
    try {
      // ReactGA4 options are: testMode, gtagUrl, gaOptions, gtagOptions
      ReactGA.initialize(ANALYTICS_CONFIG.GA_ID, {
        testMode: !ANALYTICS_CONFIG.ENABLED,
        gaOptions: {
          siteSpeedSampleRate: 100, // Collect site speed data for all users
          debug_mode: ANALYTICS_CONFIG.DEBUG, // Set debug mode via gaOptions
        },
      });
      gaInitialized = true;
      console.log(
        `Google Analytics initialized with ID: ${ANALYTICS_CONFIG.GA_ID}`,
      );
    } catch (error) {
      console.error("Google Analytics initialization failed:", error);
    }
  } else {
    console.warn(
      "Google Analytics ID not configured. Analytics will not be tracked.",
    );
  }

  // Initialize Hotjar
  if (ANALYTICS_CONFIG.HOTJAR_ID) {
    try {
      Hotjar.init(ANALYTICS_CONFIG.HOTJAR_ID, ANALYTICS_CONFIG.HOTJAR_VERSION);
      hotjarInitialized = true;
      console.log(
        `Hotjar initialized with Site ID: ${ANALYTICS_CONFIG.HOTJAR_ID}, Version: ${ANALYTICS_CONFIG.HOTJAR_VERSION}`,
      );
    } catch (error) {
      console.error("Hotjar initialization failed:", error);
    }
  } else {
    console.warn("Hotjar ID not configured. User behavior tracking disabled.");
  }

  analyticsInitialized = true;
};

/**
 * Hook to identify the current user
 */
export const useAnalyticsUser = (
  userId?: string,
  userProps?: Record<string, any>,
): void => {
  useEffect(() => {
    if (!userId || !ANALYTICS_CONFIG.ENABLED) return;

    try {
      // Set user ID for Google Analytics
      if (gaInitialized) {
        ReactGA.set({
          userId,
          ...(userProps || {}),
        });
      }

      // Identify user in Hotjar
      if (hotjarInitialized) {
        Hotjar.identify(userId, userProps || {});
      }
    } catch (error) {
      console.error("Failed to identify user in analytics:", error);
    }
  }, [userId, userProps]);
};

/**
 * Enhanced trackEvent that can accept either:
 * 1. Traditional parameters (category, action, label, value)
 * 2. Simple event name with event data object
 * 3. JSON string format (for TypeScript compatibility)
 */
export const trackEvent = (
  categoryOrEventName: string,
  actionOrEventData?: string | Record<string, any>,
  label?: string,
  value?: number,
  nonInteraction: boolean = false,
  additionalParams?: Record<string, any>,
): void => {
  if (!ANALYTICS_CONFIG.ENABLED && !ANALYTICS_CONFIG.DEBUG) return;

  let eventCategory: string;
  let eventAction: string;
  let eventLabel: string | undefined;
  let eventValue: number | undefined;
  let eventData: Record<string, any> = {};

  try {
    // Handle JSON string format (for TypeScript compatibility)
    if (typeof actionOrEventData === "string" && actionOrEventData.startsWith('{')) {
      try {
        const parsedData = JSON.parse(actionOrEventData);
        actionOrEventData = parsedData;
      } catch (e) {
        // Not JSON, treat as regular string
      }
    }

    // Handle the simplified API (eventName, eventData)
    if (typeof actionOrEventData === "object" && actionOrEventData !== null) {
      // This is the simplified API being used in App.tsx
      eventCategory = categoryOrEventName;
      eventAction = actionOrEventData.action || "triggered";
      eventData = actionOrEventData;
      
      // Extract common properties
      eventLabel = actionOrEventData.label;
      eventValue = actionOrEventData.value;

      // Track in Google Analytics with the simplified format
      if (gaInitialized) {
        ReactGA.event({
          category: eventCategory,
          action: eventAction,
          label: eventLabel,
          value: eventValue,
          ...eventData,
        });
      }

      // Track in Hotjar
      if (hotjarInitialized) {
        Hotjar.event(eventCategory);
      }

      // Send to backend
      sendToBackendAnalytics({
        type: 'event',
        category: eventCategory,
        action: eventAction,
        label: eventLabel,
        value: eventValue,
        data: eventData,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });

      if (ANALYTICS_CONFIG.DEBUG) {
        console.debug(
          `[Analytics] Event tracked (simplified): ${eventCategory}`,
          eventData,
        );
      }
      return;
    }

    // Traditional API - original implementation
    eventCategory = categoryOrEventName;
    eventAction = actionOrEventData as string || categoryOrEventName;
    eventLabel = label;
    eventValue = value;

    // Track in Google Analytics
    if (gaInitialized) {
      ReactGA.event({
        category: eventCategory,
        action: eventAction,
        label: eventLabel,
        value: eventValue,
        nonInteraction,
        ...(additionalParams || {}),
      });
    }

    // Track in Hotjar
    if (hotjarInitialized) {
      const eventName =
        additionalParams?.name || `${eventCategory}_${eventAction}`;
      Hotjar.event(eventName);
    }

    // Send to backend
    sendToBackendAnalytics({
      type: 'event',
      category: eventCategory,
      action: eventAction,
      label: eventLabel,
      value: eventValue,
      data: additionalParams,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });

    if (ANALYTICS_CONFIG.DEBUG) {
      console.debug(
        `[Analytics] Event tracked: ${eventCategory}, ${eventAction}, ${eventLabel || "N/A"}`,
      );
    }
  } catch (error) {
    console.error("Analytics event tracking failed:", error);
  }
};

/**
 * Track page views across all analytics platforms
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!ANALYTICS_CONFIG.ENABLED && !ANALYTICS_CONFIG.DEBUG) {
    return;
  }

  try {
    // Track in Google Analytics
    if (gaInitialized) {
      ReactGA.send({
        hitType: "pageview",
        page: path,
        title: title,
      });
    }

    // Track in Hotjar
    if (hotjarInitialized) {
      Hotjar.stateChange(path);
    }

    // Send to backend
    sendToBackendAnalytics({
      type: 'pageview',
      path,
      title: title || document.title,
      referrer: document.referrer,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });

    if (ANALYTICS_CONFIG.DEBUG) {
      console.debug(`[Analytics] Page view tracked: ${path}`);
    }
  } catch (error) {
    console.error("Page view tracking failed:", error);
  }
};

/**
 * Track exceptions
 */
export const trackException = (
  description: string,
  fatal: boolean = false,
): void => {
  if (!ANALYTICS_CONFIG.ENABLED && !ANALYTICS_CONFIG.DEBUG) return;

  try {
    // Track in Google Analytics - GA4 uses events instead of exception
    if (gaInitialized) {
      ReactGA.event({
        category: "Exception",
        action: fatal ? "Fatal Error" : "Error",
        label: description,
        nonInteraction: true,
      });
    }

    if (ANALYTICS_CONFIG.DEBUG) {
      console.debug(
        `[Analytics] Exception tracked: ${description}, Fatal: ${fatal}`,
      );
    }
  } catch (error) {
    console.error("Exception tracking failed:", error);
  }
};

/**
 * Track form timing
 */
export const trackTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string,
): void => {
  if (!ANALYTICS_CONFIG.ENABLED && !ANALYTICS_CONFIG.DEBUG) return;

  try {
    // Track in Google Analytics (GA4 doesn't support timing directly, so use custom event)
    if (gaInitialized) {
      ReactGA.event({
        category: "Timing",
        action: category,
        label: `${variable}${label ? ` - ${label}` : ""}`,
        value: value,
      });
    }

    if (ANALYTICS_CONFIG.DEBUG) {
      console.debug(
        `[Analytics] Timing tracked: ${category}, ${variable}, ${value}ms`,
      );
    }
  } catch (error) {
    console.error("Timing tracking failed:", error);
  }
};

/**
 * Track user engagement
 */
export const trackEngagement = (
  action: string,
  label?: string,
  value?: number,
): void => {
  trackEvent("Engagement", action, label, value);
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (
  feature: string,
  action: string = "Used",
  label?: string,
  value?: number,
): void => {
  trackEvent("Feature", `${feature} - ${action}`, label, value);
};

/**
 * Analytics Hook for React components - automatically tracks page views
 */
export const usePageTracking = (path?: string): void => {
  useEffect(() => {
    if (path) {
      trackPageView(path);
    } else if (typeof window !== "undefined") {
      trackPageView(window.location.pathname + window.location.search);
    }
  }, [path]);
};

// Track page views
export const trackPageViewVercel = (path: string) => {
  analyticsInstance.track('page_view', {
    path,
    timestamp: new Date().toISOString(),
    referrer: document.referrer,
    userAgent: navigator.userAgent
  });
};

// Track custom events
export const trackEventVercel = (eventName: string, properties: Record<string, any> = {}) => {
  analyticsInstance.track(eventName, {
    ...properties,
    timestamp: new Date().toISOString(),
    path: window.location.pathname
  });
};

// Track user identification
export const identifyUserVercel = (userId: string, traits: Record<string, any> = {}) => {
  analyticsInstance.identify(userId, {
    ...traits,
    timestamp: new Date().toISOString()
  });
};

// Track onboarding events
export const trackOnboardingEvent = (step: string, properties: Record<string, any> = {}) => {
  if (!ANALYTICS_CONFIG.ENABLED && !ANALYTICS_CONFIG.DEBUG) return;

  try {
    // Track in Google Analytics
    if (gaInitialized) {
      ReactGA.event({
        category: 'Onboarding',
        action: step,
        ...properties,
      });
    }

    // Track in Hotjar
    if (hotjarInitialized) {
      Hotjar.event(`onboarding_${step}`);
    }

    // Track in Vercel Analytics
    analyticsInstance.track(`onboarding_${step}`, properties);

    if (ANALYTICS_CONFIG.DEBUG) {
      console.debug(`[Analytics] Onboarding event tracked: ${step}`, properties);
    }
  } catch (error) {
    console.error('Onboarding event tracking failed:', error);
  }
};

// Track authentication events
export const trackAuthEventVercel = (action: string, properties: Record<string, any> = {}) => {
  trackEventVercel('auth_event', {
    action,
    ...properties
  });
};

// Track subscription events
export const trackSubscriptionEventVercel = (action: string, properties: Record<string, any> = {}) => {
  trackEventVercel('subscription_event', {
    action,
    ...properties
  });
};

// Track error events
export const trackErrorVercel = (error: Error, context: Record<string, any> = {}) => {
  trackEventVercel('error', {
    message: error.message,
    stack: error.stack,
    ...context
  });
};

// Hook to track page views automatically
export const usePageViewTrackingVercel = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageViewVercel(location.pathname);
  }, [location]);
};

// Hook to identify user
export const useAnalyticsUserVercel = (userId?: string) => {
  useEffect(() => {
    if (userId) {
      identifyUserVercel(userId);
    }
  }, [userId]);
};

// Initialize analytics as soon as the file is imported
initializeAnalytics();

// Export as default object
export default {
  initializeAnalytics,
  trackEvent,
  trackPageView,
  trackException,
  trackTiming,
  trackEngagement,
  trackFeatureUsage,
  useAnalyticsUser,
  usePageTracking,
  trackPageViewVercel,
  trackEventVercel,
  identifyUserVercel,
  trackOnboardingEvent,
  trackAuthEventVercel,
  trackSubscriptionEventVercel,
  trackErrorVercel,
  usePageViewTrackingVercel,
  useAnalyticsUserVercel,
};
